// Laser visuals: charging telegraph + firing pulse beam. Inspired by Xerath's
// ultimate from League of Legends — the line of fire is fully telegraphed
// before the actual damage tick.

import * as THREE from "three";

export class LaserSystem {
  group = new THREE.Group();
  // One persistent telegraph line per owner; created lazily when they start charging.
  private telegraphs = new Map<string, Telegraph>();
  // Active fire beams (one-shot fading meshes)
  private beams: FireBeam[] = [];

  constructor() { this.group.name = "Lasers"; }

  // Update charging telegraphs. Called every frame.
  updateTelegraphs(activeChargers: { id: string; from: THREE.Vector3; dir: THREE.Vector3; chargeT: number; chargeMax: number; range: number }[]) {
    const seen = new Set<string>();
    for (const a of activeChargers) {
      seen.add(a.id);
      let t = this.telegraphs.get(a.id);
      if (!t) {
        t = this.makeTelegraph();
        this.group.add(t.tube);
        this.group.add(t.endRing);
        this.telegraphs.set(a.id, t);
      }
      const end = a.from.clone().addScaledVector(a.dir, a.range);
      const mid = a.from.clone().add(end).multiplyScalar(0.5);
      const upY = new THREE.Vector3(0, 1, 0);
      t.tube.position.copy(mid);
      t.tube.quaternion.setFromUnitVectors(upY, a.dir);
      const fill = 1 - a.chargeT / a.chargeMax;
      const tubeRadius = 0.25 + fill * 0.7;
      t.tube.scale.set(tubeRadius, a.range * 0.5, tubeRadius);
      t.tubeMat.opacity = 0.18 + fill * 0.55;
      t.tubeMat.color.setRGB(1, 0.22 + 0.15 * Math.sin(performance.now() * 0.012), 0.18);

      t.endRing.position.copy(end);
      t.endRing.lookAt(a.from);
      const ringScale = 0.7 + fill * 1.4;
      t.endRing.scale.setScalar(ringScale);
      t.endRingMat.opacity = 0.45 + fill * 0.45;
    }
    for (const [id, t] of this.telegraphs) {
      if (!seen.has(id)) {
        this.group.remove(t.tube);
        this.group.remove(t.endRing);
        t.tube.geometry.dispose(); t.tubeMat.dispose();
        t.endRing.geometry.dispose(); t.endRingMat.dispose();
        this.telegraphs.delete(id);
      }
    }
  }

  // Spawn a one-shot beam pulse (called when laser fires).
  spawnFire(from: THREE.Vector3, dir: THREE.Vector3, length: number, blocked: boolean) {
    const geo = new THREE.CylinderGeometry(2.4, 2.4, length, 16, 1, true);
    const mat = new THREE.MeshBasicMaterial({
      color: blocked ? 0xff7766 : 0xff2222,
      transparent: true, opacity: 1,
      side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const upY = new THREE.Vector3(0, 1, 0);
    const mid = from.clone().addScaledVector(dir, length / 2);
    mesh.position.copy(mid);
    mesh.quaternion.setFromUnitVectors(upY, dir);
    this.group.add(mesh);

    // Bright core
    const coreGeo = new THREE.CylinderGeometry(0.8, 0.8, length, 8, 1, true);
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0xffe6e0, transparent: true, opacity: 1,
      side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.position.copy(mid);
    core.quaternion.copy(mesh.quaternion);
    this.group.add(core);

    this.beams.push({ outer: mesh, outerMat: mat, core, coreMat, life: 0.55, maxLife: 0.55 });
  }

  step(dt: number) {
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i];
      b.life -= dt;
      if (b.life <= 0) {
        this.group.remove(b.outer);
        this.group.remove(b.core);
        b.outer.geometry.dispose(); b.outerMat.dispose();
        b.core.geometry.dispose();  b.coreMat.dispose();
        this.beams.splice(i, 1);
      } else {
        const t = 1 - b.life / b.maxLife;
        b.outerMat.opacity = (1 - t) * 0.9;
        b.coreMat.opacity = (1 - t);
        // Slight pulse
        const s = 1 + Math.sin(t * 18) * 0.07;
        b.outer.scale.x = s; b.outer.scale.z = s;
      }
    }
  }

  private makeTelegraph(): Telegraph {
    const tubeGeo = new THREE.CylinderGeometry(1, 1, 2, 12, 1, true);
    const tubeMat = new THREE.MeshBasicMaterial({
      color: 0xff3030, transparent: true, opacity: 0.25,
      side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const tube = new THREE.Mesh(tubeGeo, tubeMat);

    const ringGeo = new THREE.RingGeometry(2.2, 3.4, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xff5050, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false,
    });
    const endRing = new THREE.Mesh(ringGeo, ringMat);
    return { tube, tubeMat, endRing, endRingMat: ringMat };
  }
}

interface Telegraph {
  tube: THREE.Mesh;
  tubeMat: THREE.MeshBasicMaterial;
  endRing: THREE.Mesh;
  endRingMat: THREE.MeshBasicMaterial;
}

interface FireBeam {
  outer: THREE.Mesh;
  outerMat: THREE.MeshBasicMaterial;
  core: THREE.Mesh;
  coreMat: THREE.MeshBasicMaterial;
  life: number;
  maxLife: number;
}
